import {pinnedSceneBg, type PinnedSceneBg} from "../../libs/sceneBgSource";
import type {BackgroundId} from "../../libs/selectorVisibility";
import {backgroundAppearanceOf, type BackgroundAppearance} from "../../libs/backgroundAppearance";

// ---------------------------------------------------------------------------
// The link tile. A saved link (/scene/:id) shows the published gallery of its
// product, the gallery its page shows. The background the link was saved with
// can be an image that the gallery does not hold: a temporary upload tile, or
// a background that an admin deleted since. That background gets a tile of its
// own, drawn with a dashed border: it is not a real background (Sergey,
// 2026-10-16). A click on it brings the link's background back. An admin can
// make an uploaded image a real one: the product's first background.
// ---------------------------------------------------------------------------

/** The background a saved scene opened with, kept to bring it back. */
export interface LinkBackground {
  /** `bgOption` as the scene loaded it: "custom" or a gallery variation. */
  bgOption: string;
  /** The image of a "custom" background, "" for a numbered one. */
  customBg: string;
  /** The image the scene pins for its numbered background (sceneBgSource.ts). */
  pin: PinnedSceneBg | null;
  /** The background fields a gallery pick replaces. */
  appearance: BackgroundAppearance;
}

/** One tile of the gallery, as this module needs it. */
export interface GalleryImage {
  variation: number;
  imageUrl: string;
}

/** The link tile: the link's background, which no gallery tile shows. */
export interface LinkTile {
  /** The image the banners show for it. */
  imageUrl: string;
  /** The upload behind the image (POST /api/v3/bg-list/first), or null. */
  uploadId: string | null;
  /**
   * The gallery background behind the image when an admin hid it on this page
   * (POST /api/v3/bg-list/first offers it again), or null.
   */
  variation: number | null;
}

/** A stored image: `cai/baker/uploads/<hash>.<ext>` or `cai/baker/bg/<hash>.<ext>`. */
const STORED_IMAGE = /\/cai\/baker\/(uploads|bg)\/([a-f0-9]{12})\.[a-z0-9]+(?:[?#].*)?$/i;

/**
 * Do two urls show the same image? A stored image is named by its content
 * hash (blob-store.ts), so an upload and its copy in the gallery match.
 */
export function sameBgImage(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const hashA = a.match(STORED_IMAGE)?.[2];
  const hashB = b.match(STORED_IMAGE)?.[2];
  return Boolean(hashA && hashB && hashA.toLowerCase() === hashB.toLowerCase());
}

/** The id of an upload (POST /api/v3/uploads) from its url, or null. */
export function uploadIdOf(url: string | null | undefined): string | null {
  const match = url?.match(STORED_IMAGE);
  return match && match[1] === "uploads" ? match[2]! : null;
}

/**
 * The background of a loaded scene (after `applySceneBgSource`), or null when
 * it has no image: a generative background, or the ALL-products view, where
 * each banner shows its own product.
 */
export function linkBackgroundOf(
  sceneId: string,
  scene: Record<string, unknown> | null | undefined
): LinkBackground | null {
  if (!scene) return null;
  // The loader's own default (useScene): a scene without the field shows 1.
  const bgOption = String(scene.bgOption ?? "1");
  const variation = Number(bgOption);
  if (bgOption !== "custom" && !(Number.isInteger(variation) && variation > 0)) return null;
  if ((scene.product as {value?: unknown} | undefined)?.value === "ALL") return null;
  return {
    bgOption,
    customBg: bgOption === "custom" && typeof scene.customBg === "string" ? scene.customBg : "",
    pin: pinnedSceneBg(sceneId, scene),
    appearance: backgroundAppearanceOf(scene),
  };
}

/**
 * The link tile, or null when a gallery tile already shows the link's
 * background. A numbered background is the gallery tile with its number,
 * unless the scene pins an image that the tile no longer shows. A "custom"
 * background is the gallery tile that shows the same image.
 *
 * A background that an admin hid on the page has no gallery tile: only a link
 * shows it, with the link tile (Sergey, 2026-10-26).
 *
 * `imageUrl` is the image the banners show for the link's background.
 */
export function linkTileOf({
  link,
  gallery,
  hidden = new Set(),
  imageUrl,
}: {
  link: LinkBackground | null;
  gallery: GalleryImage[];
  /** The backgrounds of `gallery` that the page hides. */
  hidden?: ReadonlySet<BackgroundId>;
  imageUrl: string | null | undefined;
}): LinkTile | null {
  if (!link || !imageUrl) return null;
  const holds = (entry: GalleryImage) =>
    link.bgOption === "custom"
      ? sameBgImage(entry.imageUrl, imageUrl)
      : entry.variation === Number(link.bgOption) &&
        (!link.pin || sameBgImage(entry.imageUrl, link.pin.url));
  if (gallery.some((entry) => !hidden.has(entry.variation) && holds(entry))) return null;
  const hiddenOwn = gallery.find((entry) => hidden.has(entry.variation) && holds(entry));
  return {imageUrl, uploadId: uploadIdOf(imageUrl), variation: hiddenOwn?.variation ?? null};
}

/** Do the banners show the link's background? */
export function showsLinkBackground(
  link: LinkBackground,
  {
    bgOption,
    customBg,
    pin,
  }: {bgOption: unknown; customBg: string | null | undefined; pin: PinnedSceneBg | null}
): boolean {
  if (String(bgOption) !== link.bgOption) return false;
  if (link.bgOption === "custom") return customBg === link.customBg;
  return (pin?.url ?? null) === (link.pin?.url ?? null);
}

/**
 * Is a gallery tile the background the banners show, on a link? A pick pins
 * the tile's image (`pinUrl`, the pin that applies now), so a tile is selected
 * only while the banners show its image. An image shown as "custom" selects
 * the tile with the same image: an upload made a real background.
 */
export function isLinkGalleryTileCurrent({
  variation,
  imageUrl,
  bgOption,
  customBg,
  pinUrl,
}: {
  variation: number;
  imageUrl: string;
  bgOption: unknown;
  customBg: string | null | undefined;
  pinUrl: string | null;
}): boolean {
  if (String(bgOption) === "custom") return sameBgImage(customBg, imageUrl);
  if (Number(bgOption) !== variation) return false;
  return !pinUrl || sameBgImage(pinUrl, imageUrl);
}
